/**
 * MobileDrawer — slide-from-left drawer wrapper for navigation sidebars
 * on viewports ≤ 768px (Phase 14.2).
 *
 * WHY THIS COMPONENT EXISTS
 *   The three navigation sidebars (AdminSidebar, AnnotatorSidebar, and
 *   the bespoke inline one in ClientPortal) all assume a permanent
 *   148-180px-wide rail next to main content. On phones and small
 *   tablets that eats half the screen. This component hides them by
 *   default below `md` (768px) and replaces with a hamburger button
 *   that opens the same nav as a slide-in drawer.
 *
 *   Built on @radix-ui/react-dialog (~1.5kb sidecar): focus trap,
 *   Esc key, click-outside, focus restoration to trigger, and
 *   `aria-modal` + role attributes are all handled by the primitive.
 *   We avoid the high-risk pattern of rolling our own focus trap.
 *
 *   `prefers-reduced-motion` honored — 200ms slide animation collapses
 *   to a simple fade for users who opt out of vestibular motion.
 *
 * USAGE
 *   <MobileDrawer
 *     trigger={<HamburgerButton />}
 *     ariaLabel="Main navigation"
 *   >
 *     <YourSidebar />
 *   </MobileDrawer>
 *
 *   The `trigger` is rendered as the visible affordance. The `children`
 *   render inside the drawer panel. `ariaLabel` is required (WCAG 4.1.2
 *   accessible name).
 *
 * CALLERS
 *   AdminSidebar.jsx  — wraps for admin/SPL navigation
 *   AnnotatorSidebar.jsx — wraps for annotator navigation
 *   ClientPortal.jsx — wraps the inline sidebar at line ~1681
 *
 * BREAKPOINT
 *   Drawer renders only when `useMediaQuery('(max-width: 768px)')`
 *   returns true. Above 768px, callers render their normal sidebar
 *   inline and the drawer component returns null. This keeps a single
 *   render tree without conditionally swapping between two sidebar
 *   instances.
 */
import * as Dialog from '@radix-ui/react-dialog';
import { useMediaQuery } from '../hooks/useMediaQuery';

export default function MobileDrawer({ trigger, ariaLabel, children }) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Above 768px the caller renders its desktop sidebar; we render nothing.
  if (!isMobile) return null;

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="mobile-drawer-overlay" />
        <Dialog.Content className="mobile-drawer-panel" aria-label={ariaLabel}>
          {/* The accessible-name source — Radix requires either a
              VisuallyHidden Title or aria-label on Content. We use
              aria-label from the caller for clarity. The inline title
              below renders for sighted users; SR users hear ariaLabel. */}
          <Dialog.Title className="mobile-drawer-title">{ariaLabel}</Dialog.Title>
          <Dialog.Description className="mobile-drawer-description-sr-only">
            Press Escape to close, or tap outside the drawer.
          </Dialog.Description>
          <Dialog.Close
            className="mobile-drawer-close"
            aria-label="Close navigation"
          >
            ✕
          </Dialog.Close>
          <div className="mobile-drawer-content">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
